// PoolScreener — operator research surface. Scans the DefiLlama universe,
// runs the six +EV screens per pool (client-side; public APIs + our subgraph
// proxy), and ranks candidates by expected annual edge. Not investor-facing.
import { Fragment, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { RefreshCw, ChevronDown, ChevronRight, Info } from 'lucide-react';
import {
  screenPools,
  clearCache,
  type ScreenedPool,
  type ScreenResult,
  type Verdict,
} from '../lib/poolscreener';
import { Button } from '../components/ui/button';
import { SectionHeader } from '../components/ui/section-header';
import { MetricCard } from '../components/ui/metric-card';
import { StatBadge } from '../components/ui/stat-badge';
import type { Column } from '../components/ui/data-table';
import { cn, formatCurrency, formatPercent } from '../lib/utils';
import type { ScreenPoolsOpts } from '../lib/poolscreener';

const SCAN_OPTS: ScreenPoolsOpts = { chains: ['Base'], tvlFloorUsd: 2_000_000, maxPools: 20 };

const VERDICT_VARIANT: Record<Verdict, 'positive' | 'negative' | 'warning' | 'neutral'> = {
  PASS: 'positive',
  FAIL: 'negative',
  WARN: 'warning',
  INSUFFICIENT_DATA: 'neutral',
};

const SCREEN_META: { key: keyof ScreenedPool['screens']; name: string; hint: string }[] = [
  { key: 'lvrBreakeven', name: 'LVR breakeven', hint: 'Daily fee return vs σ²/8 LVR bleed. PASS = fees win.' },
  { key: 'flowToxicity', name: 'Flow toxicity', hint: 'CEX-listed + spiky volume (CoV>1.5) → arb-dominated flow.' },
  { key: 'ivRv', name: 'IV / RV', hint: 'Lambert pool-implied vol vs realized. PASS = pool overcharges for risk.' },
  { key: 'funding', name: 'Funding carry', hint: 'Trailing perp funding, annualized. PASS = funding pays the short.' },
  { key: 'liqHeadroom', name: 'Liq headroom', hint: 'Short liq price clears range top ×1.15. PASS = safe leverage.' },
  { key: 'emissions', name: 'Emissions', hint: 'Aerodrome only: emissions coverage of LVR + AERO price trend.' },
];

function fmtNum(v: number): string {
  if (!Number.isFinite(v)) return '—';
  const abs = Math.abs(v);
  if (abs !== 0 && abs < 0.001) return v.toExponential(2);
  if (abs >= 1000) return v.toLocaleString('en-US', { maximumFractionDigits: 0 });
  return v.toFixed(abs < 1 ? 4 : 3);
}

function VerdictBadge({ result }: { result: ScreenResult }) {
  const label =
    result.verdict === 'INSUFFICIENT_DATA' ? 'N/A' : result.verdict.charAt(0) + result.verdict.slice(1).toLowerCase();
  return <StatBadge label={label} variant={VERDICT_VARIANT[result.verdict]} dot />;
}

function ScreenDetail({ name, hint, result }: { name: string; hint: string; result: ScreenResult }) {
  return (
    <div className="rounded-lg border border-border bg-surface p-3">
      <div className="flex items-center justify-between gap-2 mb-1">
        <span className="text-sm font-medium text-foreground">{name}</span>
        <VerdictBadge result={result} />
      </div>
      <p className="text-xs text-muted-foreground mb-2">{hint}</p>
      <dl className="grid grid-cols-2 gap-x-3 gap-y-1">
        {Object.entries(result.detail).map(([k, v]) => (
          <div key={k} className="flex items-center justify-between gap-2 text-xs">
            <dt className="text-muted-foreground truncate">{k}</dt>
            <dd className="font-mono text-foreground">{fmtNum(v)}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function PoolRow({ pool }: { pool: ScreenedPool }) {
  return (
    <div className="border-t border-border bg-muted/20 px-4 py-4">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {SCREEN_META.map(({ key, name, hint }) => {
          const result = pool.screens[key];
          if (!result) return null;
          return <ScreenDetail key={key} name={name} hint={hint} result={result} />;
        })}
      </div>
      <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-xs text-muted-foreground">
        <span>
          edge ={' '}
          <span className="text-foreground">
            {pool.feeEdgeApr === null ? '—' : formatPercent(pool.feeEdgeApr * 100, 1)}
          </span>{' '}
          durable (fees + funding − LVR)
          {pool.emissionsEdgeApr > 0 && (
            <>
              {' + '}
              <span className="text-warning">{formatPercent(pool.emissionsEdgeApr * 100, 1)}</span>{' '}
              emissions (volatile)
            </>
          )}
        </span>
        <span>
          churn: ~<span className="text-foreground">{pool.exitsPerYear.toFixed(0)}</span> exits/yr
          {Number.isFinite(pool.daysInRange) && <> (~{pool.daysInRange.toFixed(0)}d in range)</>}
          {pool.feesPerEpoch !== null && <>, {formatPercent(pool.feesPerEpoch * 100, 2)} fees/epoch</>},
          drag −{formatPercent(pool.churnDragApr * 100, 1)} → net{' '}
          <span className="text-foreground">
            {pool.netApr === null ? '—' : formatPercent(pool.netApr * 100, 1)}
          </span>
        </span>
        <span>
          pool detail source: <span className="text-foreground">{pool.detail.source}</span>
        </span>
        {pool.feeTierDerived && (
          <span className="text-warning">fee tier derived from realized APR (no on-chain tier)</span>
        )}
        {pool.detail.poolAddress && (
          <span className="font-mono">pool {pool.detail.poolAddress.slice(0, 10)}…</span>
        )}
      </div>
    </div>
  );
}

export default function Screener() {
  const [expanded, setExpanded] = useState<string | null>(null);
  const { data, isFetching, error, refetch } = useQuery({
    queryKey: ['screener', SCAN_OPTS],
    queryFn: () => screenPools(SCAN_OPTS),
    staleTime: 60 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const pools = data ?? [];
  const scored = pools.filter((p) => p.netApr !== null);
  const positive = scored.filter((p) => (p.netApr ?? 0) > 0);
  const topNet = scored[0]?.netApr ?? null;

  async function rescan() {
    clearCache();
    await refetch();
  }

  const columns: Column<ScreenedPool>[] = [
    {
      key: 'pool',
      header: 'Pool',
      cell: (p) => (
        <div className="flex items-center gap-2">
          {expanded === p.pool.poolId ? (
            <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
          ) : (
            <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
          )}
          <div className="min-w-0">
            <div className="font-medium text-foreground truncate">{p.pool.symbol}</div>
            <div className="text-xs text-muted-foreground truncate">
              {p.pool.project} · {p.pool.chain}
            </div>
          </div>
        </div>
      ),
    },
    { key: 'tvl', header: 'TVL', cell: (p) => formatCurrency(p.detail.tvlUsd, true), className: 'tabular-nums' },
    {
      key: 'vol',
      header: 'Vol/day (90d)',
      cell: (p) => formatCurrency(p.detail.dailyVolumeUsd, true),
      className: 'tabular-nums',
    },
    {
      key: 'fee',
      header: 'Fee',
      cell: (p) => (
        <span className="tabular-nums">
          {(p.feeTier * 100).toFixed(3)}%{p.feeTierDerived ? '*' : ''}
        </span>
      ),
    },
    {
      key: 'exits',
      header: 'Exits/yr',
      headerClassName: 'text-right',
      className: 'text-right',
      cell: (p) => (
        <span
          className="tabular-nums text-muted-foreground"
          title="Expected range exits (≈ redeploys) per year, from vol + range width"
        >
          {p.exitsPerYear > 0 ? `~${p.exitsPerYear.toFixed(0)}` : '—'}
        </span>
      ),
    },
    {
      key: 'screens',
      header: 'Screens',
      cell: (p) => {
        const applicable = SCREEN_META.map((m) => p.screens[m.key]).filter(Boolean) as ScreenResult[];
        const total = applicable.length;
        return (
          <div className="flex items-center gap-1.5">
            <span className="text-xs tabular-nums text-muted-foreground">
              {p.passCount}/{total}
            </span>
            <div className="flex gap-0.5">
              {applicable.map((s, i) => (
                <span
                  key={i}
                  title={s.verdict}
                  className={cn(
                    'h-2 w-2 rounded-full',
                    s.verdict === 'PASS' && 'bg-positive',
                    s.verdict === 'FAIL' && 'bg-negative',
                    s.verdict === 'WARN' && 'bg-warning',
                    s.verdict === 'INSUFFICIENT_DATA' && 'bg-muted-foreground/40',
                  )}
                />
              ))}
            </div>
          </div>
        );
      },
    },
    {
      key: 'net',
      header: 'Net APR',
      headerClassName: 'text-right',
      className: 'text-right',
      cell: (p) => {
        if (p.netApr === null) return <span className="text-muted-foreground">—</span>;
        // Flag rows whose edge is mostly Aerodrome emissions (volatile / decays).
        const emisHeavy = p.emissionsEdgeApr > (p.feeEdgeApr ?? 0) && p.emissionsEdgeApr > 0;
        return (
          <div
            className="flex items-center justify-end gap-1.5"
            title={`gross ${
              p.expectedEdgeAnnual === null ? '—' : formatPercent(p.expectedEdgeAnnual * 100, 1)
            } − churn ${formatPercent(p.churnDragApr * 100, 1)}`}
          >
            {emisHeavy && (
              <span
                title="Edge is mostly Aerodrome emissions — volatile and decays; not durable fee yield"
                className="text-[10px] font-semibold uppercase text-warning"
              >
                emis
              </span>
            )}
            <span
              className={cn(
                'font-medium tabular-nums',
                p.netApr > 0 ? 'text-positive' : 'text-negative',
              )}
            >
              {formatPercent(p.netApr * 100, 1)}
            </span>
          </div>
        );
      },
    },
  ];

  return (
    <div className="space-y-6">
      <SectionHeader
        title="Pool Screener"
        subtitle="Ranks by NET APR — gross edge (fees/emissions + funding − LVR) minus redeploy churn (range exits × cost)."
        actions={
          <Button variant="outline" size="sm" onClick={rescan} disabled={isFetching}>
            <RefreshCw className={cn('h-4 w-4', isFetching && 'animate-spin')} />
            {isFetching ? 'Scanning…' : 'Rescan'}
          </Button>
        }
      />

      <div className="grid gap-4 sm:grid-cols-3">
        <MetricCard label="Pools scored" value={`${scored.length} / ${pools.length}`} />
        <MetricCard label="Positive net APR" value={String(positive.length)} />
        <MetricCard
          label="Top net APR"
          value={topNet === null ? '—' : formatPercent(topNet * 100, 1)}
        />
      </div>

      {error ? (
        <div className="rounded-lg border border-border bg-surface p-6 text-sm text-negative">
          Screener failed to load. Public data sources may be rate-limited — try Rescan in a moment.
        </div>
      ) : (
        <div className="rounded-lg border border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/40">
                {columns.map((col) => (
                  <th
                    key={col.key}
                    className={cn(
                      'px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider whitespace-nowrap',
                      col.headerClassName,
                    )}
                  >
                    {col.header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isFetching && pools.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} className="px-4 py-10 text-center text-sm text-muted-foreground">
                    Scanning the universe — fetching pool detail, price history, and funding…
                  </td>
                </tr>
              ) : pools.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} className="px-4 py-10 text-center text-sm text-muted-foreground">
                    No pools matched the scan filters.
                  </td>
                </tr>
              ) : (
                pools.map((p) => (
                  <Fragment key={p.pool.poolId}>
                    <tr
                      onClick={() => setExpanded(expanded === p.pool.poolId ? null : p.pool.poolId)}
                      className="border-b border-border last:border-0 cursor-pointer hover:bg-accent/40 transition-colors"
                    >
                      {columns.map((col) => (
                        <td key={col.key} className={cn('px-4 py-3', col.className)}>
                          {col.cell(p)}
                        </td>
                      ))}
                    </tr>
                    {expanded === p.pool.poolId && (
                      <tr>
                        <td colSpan={columns.length} className="p-0">
                          <PoolRow pool={p} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex items-start gap-2 text-xs text-muted-foreground">
        <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
        <p>
          Edge is a ranking heuristic, not a promise. Rows without a CEX-listed volatile leg lack
          vol/funding data and score no edge. A <span className="font-mono">*</span> fee tier was
          derived from realized APR when no on-chain tier was available. Data cached 1h; Rescan
          forces a refresh.
        </p>
      </div>
    </div>
  );
}
